import { useEffect } from 'react';
import { Chart as ChartJS } from 'chart.js';
import { Theme, useTheme } from '@mui/material';

/**
 * Chart.js styled from the MUI theme, for every chart in the app.
 *
 * The charts used to carry their own fonts, near-black tooltips and rgba(0,0,0,0.05) gridlines,
 * so they looked bolted on — and would have stayed black-on-black in dark mode. Chart.js reads
 * these defaults when a chart draws, so setting them from the theme (and again whenever the theme
 * changes) restyles every chart without touching each one. Charts should not hard-code colours
 * for text, gridlines or tooltips; series colours are theirs to choose.
 */
export function applyChartTheme(theme: Theme) {
  const dark = theme.palette.mode === 'dark';
  const d = ChartJS.defaults;
  d.font.family = String(theme.typography.fontFamily);
  d.font.size = 12;
  d.color = theme.palette.text.secondary;
  d.borderColor = theme.palette.divider;

  d.plugins.legend.labels.usePointStyle = true;
  d.plugins.legend.labels.boxWidth = 8;
  d.plugins.legend.labels.padding = 16;

  const t = d.plugins.tooltip;
  t.backgroundColor = dark ? theme.palette.grey[800] : '#111827';
  t.titleColor = '#fff';
  t.bodyColor = '#e5e7eb';
  t.borderColor = dark ? theme.palette.grey[700] : 'transparent';
  t.borderWidth = dark ? 1 : 0;
  t.padding = 10;
  t.cornerRadius = 8;
  t.boxPadding = 4;
  t.usePointStyle = true;
  t.titleFont = { weight: 600 };

  d.elements.line.tension = 0.35;
  d.elements.point.radius = 0;
  d.elements.point.hoverRadius = 4;
  d.elements.bar.borderRadius = 4;
}

/** Keeps Chart.js in step with the current MUI theme. Mounted once, in Layout. */
export function useChartTheme() {
  const theme = useTheme();
  useEffect(() => { applyChartTheme(theme); }, [theme]);
}

/** Colours for the engagement metrics, shared by the tiles and the charts so they match. */
export const METRIC_COLORS = {
  likes: '#e91e63',
  comments: '#1976d2',
  shares: '#10b981',
  impressions: '#9c27b0',
} as const;

/** A hex colour at an alpha, for chart fills: withAlpha('#e91e63', 0.12). */
export const withAlpha = (hex: string, alpha: number) => {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};
